import React, { useCallback, useEffect, useState } from "react";
import { Topbar, CalendarModal } from "@components";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Radio, Spin } from "antd";
import {
  CalendarFilled,
} from "@assets";
import { PRIVATE_ROUTE } from "@router/route.constants";
import { api } from "@api";
import { LIMITS, TOPUP_SK, WITHDRAW_SK, BONUS_SK } from "@constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { getFormattedNumber } from "@utils";
import moment from "moment";
import { BonusImage } from "../Wallet/Wallet";
import { useTranslation } from "react-i18next";
import { getStatus } from "@utils";
import {
  AmountText,
  DateText,
  ExpiredLabel,
  ExpiredText,
  Image,
  ItemText,
  ListContainer,
  ListItem,
  StatusText,
  Wrapper,
  EmptyText,
  BonusStatusText,
  DataWrapper,
} from "../Wallet/Wallet.style";
import { ContainerOutlined } from "@ant-design/icons";

const RootContainer = styled.div`
  padding: 16px 0;
  height: calc(100vh - 102px);
  overflow: hidden;

  .empty-box {
    height: calc(100vh - 140px);
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    justify-content: center;

    svg {
      font-size: 24px;
      color: #808080;
    }
  }

  .anticon .anticon-calendar {
    font-size: 28px;
    color: #fff;
  }
`;

const StyledListWrapper = styled(ListContainer).attrs({
  id: "scrollable-div",
})`
  height: calc(100vh - 140px);
  padding-top: 0 !important;
`;

const SpinWrapper = styled.div`
  height: calc(100vh - 140px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  align-items: center;
  .ant-radio-wrapper span.ant-radio + * {
    padding-inline-start: 0px !important;
  }
  margin: 0px 16px 16px 16px;
`;

export const RadioButton = styled(Radio)`
  .ant-radio {
    opacity: 0;
    position: fixed;
    width: 0;
  }
  margin-inline-end: 0px !important;
`;

export const Text = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => (props.status ? "#FFFFFF" : "#000")};
  background: ${(props) => (props.status ? "linear-gradient(90deg, #0060BE 0%, #04AEED 100%)" : "#DEEDFF")};
  padding: 6px 16px 6px 16px;
  border-radius: 23px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  min-width: 30px;
  @media screen and (max-width: 360px) {
    padding: 4px 14px 4px 14px;
  }
  @media screen and (max-width: 355px) {
    font-size: 10px;
  }
  @media screen and (max-width: 320px) {
    padding: 4px 12px 4px 12px;
  }
  @media screen and (max-width: 300px) {
    padding: 2px 8px 2px 8px;
    font-size: 9px;
  }
`;

const { DEFAULT_PAGE, PAGE_SIZE } = LIMITS;

const dateFormat = "MMM DD, YYYY hh:mm:ss A";
const apiDateFormat = "YYYY-MM-DD";

const TransactionHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [type, setType] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);

  const getTransactions = useCallback(() => {
    setLoading(true);
    api
      .getTransactions({
        page,
        limit: PAGE_SIZE,
        type,
        start_date: date,
        end_date: date,
      })
      .then(({ transactions, pagination }) => {
        setData((prevState) => {
          return [...prevState, ...transactions];
        });
        setPage(pagination.page);
        setTotal(pagination.total);
        setLoading(false);
      })
      .catch(() => { });
  }, [type, page, date]);

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const onChange = (e) => {
    setType(e.target.value);
    setPage(DEFAULT_PAGE);
    setData([]);
    setTotal(0);
  };

  const onOk = (newdate) => {
    newdate = newdate.format(apiDateFormat);
    if (newdate !== date) {
      setDate(newdate);
      setType("");
      setPage(DEFAULT_PAGE);
      setData([]);
      setTotal(0);
    }
    handleClose();
  };

  const onReset = () => {
    if (date) {
      setDate("");
      setType("");
      setPage(DEFAULT_PAGE);
      setData([]);
      setTotal(0);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const goBack = () => {
    navigate(`/${PRIVATE_ROUTE.WALLET}`);
  };

  const renderTop = () => (
    <RadioGroup onChange={onChange} value={type}>
      <RadioButton value={""}>
        <Text status={type === ""}>
          {t("wallet.transactionHistoryHeadingAll")}
        </Text>
      </RadioButton>
      <RadioButton value={WITHDRAW_SK}>
        <Text status={type === WITHDRAW_SK}>
          {t("wallet.walletWithdraw")}
        </Text>
      </RadioButton>
      <RadioButton value={TOPUP_SK}>
        <Text status={type === TOPUP_SK}>
          {t("wallet.walletTopUp")}
        </Text>
      </RadioButton>
      <RadioButton value={BONUS_SK}>
        <Text status={type === BONUS_SK}>
          {t("wallet.transactionHistoryHeadingBonus")}
        </Text>
      </RadioButton>
    </RadioGroup>
  );

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  const EmptyCard = () => (
    <div className="empty-box">
      <ContainerOutlined />
      <EmptyText>{t("common.noRecord")}</EmptyText>
    </div>
  );

  return (
    <>
      <Topbar
        shadow={false}
        title={t("wallet.transactionHistoryTitle")}
        rightIcon={<CalendarFilled onClick={() => setOpen(true)} />}
        goBack={goBack}
      />
      <RootContainer>
        {renderTop()}
        <StyledListWrapper>
          <InfiniteScroll
            dataLength={data.length}
            next={() => setPage((prev) => prev + 1)}
            hasMore={loading || data.length < total}
            loader={<Loader />}
            scrollableTarget="scrollable-div"
            endMessage={!loading && data.length === 0 && <EmptyCard />}
          >
            {data.map((item, key) => (
              <ListItem key={key}>
                {item.type === BONUS_SK ? (
                  <BonusImage />
                ) : (
                  <Image
                    src={item.payment_category_image}
                    radius={
                      item.payment_category_name === "KBZ" ? "none" : "circle"
                    }
                  />
                )}
                <DataWrapper>
                  <Wrapper>
                    <ItemText>
                      {item.type === BONUS_SK
                        ? t("wallet.transactionHistoryBonusType", {
                          bonus_type: item.bonus_type,
                        })
                        : `${item.type === WITHDRAW_SK
                          ? t("wallet.walletWithdraw")
                          : t("wallet.walletTopUp")
                        } ${t("common.to")} ${item.num}`}
                    </ItemText>
                    <AmountText>
                      {`${getFormattedNumber(item.amount)} Ks`}
                    </AmountText>
                  </Wrapper>
                  <Wrapper>
                    <DateText>
                      {moment.unix(item.created_at).format(dateFormat)}
                    </DateText>
                  </Wrapper>
                  <Wrapper>
                    {item.trx_num && (
                      <DateText>{`PIN: ${item.trx_num}`}</DateText>
                    )}
                    {item.type === BONUS_SK && item.bonus_expired && (
                      <div style={{ display: "flex" }}>
                        <ExpiredLabel>{t("wallet.expiredLabel")}</ExpiredLabel>
                        <ExpiredText>
                          &nbsp;&nbsp;
                          {moment.unix(item.bonus_expired).format(dateFormat)}
                        </ExpiredText>
                      </div>
                    )}
                  </Wrapper>
                  <Wrapper>
                    {item.type === BONUS_SK && item.bonus_expired && (
                      <BonusStatusText status={item.status}>
                        {item.status === "Used"
                          ? t("wallet.bonusUsedText")
                          : t("wallet.bonusNotUsedText")}
                      </BonusStatusText>
                    )}
                    {item.type !== BONUS_SK && (
                      <StatusText status={item.status}>
                        {getStatus(item.status)}
                      </StatusText>
                    )}
                  </Wrapper>
                </DataWrapper>
              </ListItem>
            ))}
          </InfiniteScroll>
        </StyledListWrapper>
      </RootContainer>
      <CalendarModal
        open={open}
        date={date}
        onOk={onOk}
        onReset={onReset}
        handleClose={handleClose}
      />
    </>
  );
};

export default TransactionHistory;
